import React, { useState } from 'react';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { atomOneDark } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import { github } from 'react-syntax-highlighter/dist/esm/styles/hljs';

const CodeBlock = ({
  code,
  language = 'javascript',
  title = null,
  showLineNumbers = true,
  variant = 'light',
}) => {
  const [copied, setCopied] = useState(false);
  const isLight = variant === 'light';
  const palette = isLight ? lightStyles : darkStyles;
  const lineCount = code.split('\n').length;
  const useLineNumbers = showLineNumbers && lineCount > 1;

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="syntax-highlighter-root" style={palette.container}>
      {title && <div style={palette.title}>{title}</div>}
      <div style={palette.header}>
        <span style={palette.language}>{language.toUpperCase()}</span>
        <button
          type="button"
          onClick={handleCopy}
          style={{
            ...palette.copyButton,
            backgroundColor: copied ? '#059669' : isLight ? '#276EF1' : '#6366f1',
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <SyntaxHighlighter
        language={language}
        style={isLight ? github : atomOneDark}
        showLineNumbers={useLineNumbers}
        wrapLongLines
        customStyle={{
          margin: 0,
          borderRadius: '0 0 8px 8px',
          fontSize: '13px',
          lineHeight: '1.55',
          padding: '12px 16px',
          background: isLight ? '#FFFFFF' : '#282c34',
          color: isLight ? '#24292e' : '#abb2bf',
        }}
        codeTagProps={{
          className: 'syntax-block-code',
        }}
        lineNumberStyle={{
          color: '#6e7781',
          minWidth: '2.25em',
          paddingRight: '1em',
          textAlign: 'right',
          userSelect: 'none',
        }}
        lineNumberContainerStyle={{
          float: 'left',
          paddingRight: '1em',
          marginRight: '0.75em',
          borderRight: isLight ? '1px solid #E8E8E8' : '1px solid #374151',
        }}
      >
        {code}
      </SyntaxHighlighter>
    </div>
  );
};

const baseContainer = {
  margin: '10px 0',
  borderRadius: '8px',
  overflow: 'hidden',
};

const lightStyles = {
  container: {
    ...baseContainer,
    backgroundColor: '#FFFFFF',
    border: '1px solid #E8E8E8',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
  },
  title: {
    backgroundColor: '#F5F5F5',
    padding: '8px 16px',
    color: '#333333',
    fontSize: '12px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: '1px solid #E8E8E8',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#FAFAFA',
    padding: '6px 12px',
    borderBottom: '1px solid #E8E8E8',
  },
  language: {
    color: '#57606a',
    fontSize: '11px',
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  copyButton: {
    padding: '4px 10px',
    fontSize: '11px',
    fontWeight: '600',
    color: '#ffffff',
    border: 'none',
    borderRadius: '999px',
    cursor: 'pointer',
  },
};

const darkStyles = {
  container: {
    ...baseContainer,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    backgroundColor: '#1f2937',
  },
  title: {
    backgroundColor: '#111827',
    padding: '8px 16px',
    color: '#f3f4f6',
    fontSize: '12px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: '1px solid #374151',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#1f2937',
    padding: '8px 16px',
    borderBottom: '1px solid #374151',
  },
  language: {
    color: '#9ca3af',
    fontSize: '11px',
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  copyButton: {
    padding: '4px 12px',
    fontSize: '11px',
    fontWeight: '600',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
};

export default CodeBlock;
